/**
 * Decimal arithmetic for mass, potency, yield and money (PROJECT_SPEC.md §6.1 "never binary
 * floating point", CALC-003/004/005/006/011). Values travel as strings end-to-end; rounding
 * happens only where a RoundingSpec says so (CALC-003), with an explicit mode (CALC-004).
 */
import { Decimal } from "decimal.js";

// 40 significant digits: intermediate values keep full precision (CALC-003).
export const D = Decimal.clone({ precision: 40, toExpNeg: -30, toExpPos: 40 });
export type Dec = InstanceType<typeof D>;

export type RoundingMode = "HALF_UP" | "HALF_EVEN" | "TRUNCATE" | "CEILING" | "FLOOR";
export interface RoundingSpec {
  /** decimal places (>= 0) */
  places: number;
  mode: RoundingMode;
}

const MODE: Record<RoundingMode, Decimal.Rounding> = {
  HALF_UP: Decimal.ROUND_HALF_UP,
  HALF_EVEN: Decimal.ROUND_HALF_EVEN,
  TRUNCATE: Decimal.ROUND_DOWN,
  CEILING: Decimal.ROUND_CEIL,
  FLOOR: Decimal.ROUND_FLOOR,
};

export class CalcError extends Error {
  constructor(
    public readonly code: "DIVISION_BY_ZERO" | "NOT_FINITE" | "INVALID_NUMBER" | "OVERFLOW",
    message: string,
  ) {
    super(message);
  }
}

const NUMERIC = /^[+-]?(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?$/;
const MAX_DIGITS = 38; // matches NUMERIC(38, x) storage bound (LIM-012)

/** Parses a user/interface string strictly; rejects NaN/Infinity/garbage (LIM-008, CALC-006). */
export function dec(value: string | number | Dec): Dec {
  if (value instanceof D) return value;
  const s = typeof value === "number" ? String(value) : value.trim();
  if (!NUMERIC.test(s)) throw new CalcError("INVALID_NUMBER", `not a decimal number: "${s}"`);
  const d = new D(s);
  if (!d.isFinite()) throw new CalcError("NOT_FINITE", `not finite: "${s}"`);
  if (d.abs().gte(new D(10).pow(MAX_DIGITS)))
    throw new CalcError("OVERFLOW", `exceeds ${MAX_DIGITS} digits: "${s}"`);
  return d;
}

export function divide(a: Dec, b: Dec): Dec {
  if (b.isZero()) throw new CalcError("DIVISION_BY_ZERO", "division by zero");
  return a.div(b);
}

/** Rounds ONLY here, per an explicit spec. Never call toFixed elsewhere. */
export function round(value: Dec, spec: RoundingSpec): Dec {
  if (!Number.isInteger(spec.places) || spec.places < 0)
    throw new CalcError("INVALID_NUMBER", `bad places ${spec.places}`);
  return value.toDecimalPlaces(spec.places, MODE[spec.mode]);
}

/** Canonical string for storage/export: plain notation, no exponent, no trailing zeros beyond spec. */
export function toPlain(value: Dec, spec?: RoundingSpec): string {
  const v = spec ? round(value, spec) : value;
  return spec ? v.toFixed(spec.places) : v.toFixed();
}

/** Sum that is exactly the sum of its constituents (CALC-014). */
export function sum(values: readonly Dec[]): Dec {
  return values.reduce((acc, v) => acc.plus(v), new D(0));
}

/** percent = part / whole × 100 (yield %, CALC-007). */
export function percent(part: Dec, whole: Dec): Dec {
  return divide(part, whole).times(100);
}
